import { useEffect, useState } from 'react';

import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import ErrorIcon from '@mui/icons-material/Error';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import toast from 'react-hot-toast';

import { usePutCommandQueueRestartMutation } from '@/store/api/services/machines/machine';

/**
 * Case Restart Command Modal
 *
 * This modal allows sending restart commands to machines associated with a case.
 * Currently supports only restart commands as requested.
 *
 * @param {ModalChildrenProps} { hide, props }
 * @return {*}
 */
const CaseRestartCommand = ({ hide, props }: ModalChildrenProps) => {
  /**
   * State to manage the command in progress status value
   */
  const [hasCommandInProgress, setHasCommandInProgress] = useState(false);

  /**
   * Get the Case Restart Command mutation
   * Uses the existing machine restart API with the case's atm_id
   */
  const [restartMachine, { isLoading }] = usePutCommandQueueRestartMutation();

  useEffect(() => {
    /**
     * Check if there's a command in progress for any machines in this case
     */
    setHasCommandInProgress(props?.caseData?.hasCommandInProgress === true);
  }, [props?.caseData]);

  const handleRestart = async () => {
    /**
     * Submit the restart command for the case using the atm_id
     */
    try {
      await restartMachine(props?.caseData?.atm_id).unwrap();
      toast.success(
        `Restart command successfully sent to machine ${props?.caseData?.atm_id} for case ${props?.caseData?.id}`,
      );
      hide();
    } catch (_error) {
      // Error handled by toast
    }
  };

  if (hasCommandInProgress) {
    return (
      <>
        <DialogContent sx={{ my: 2 }}>
          <Alert severity="error" icon={<ErrorIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Command Already In Progress
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              There is already a command in progress for machines in this case. Please wait for the
              current command to complete before sending a new one.
            </Typography>
          </Alert>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Button onClick={hide}>Back to Case</Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This will send a restart command to machine <strong>{props?.caseData?.atm_id}</strong>{' '}
          associated with case <strong>{props?.caseData?.id}</strong>.
        </Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Warning
          </Typography>
          <Typography variant="body2">
            This action will restart the machine associated with this case. Make sure this is what
            you intend to do. The restart process may take a few minutes to complete.
          </Typography>
        </Alert>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide} variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleRestart}
          variant="contained"
          color="error"
          loading={isLoading}
          disabled={isLoading}
        >
          Send Restart Command
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CaseRestartCommand;
